import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  submitHealthExemption,
  uploadHealthExemptionDocument,
} from "@/lib/healthOperations";
import { typeAppliesToChild } from "@/lib/childHealth";

const MAX_SUPPORTING_DOCUMENT_BYTES = 10 * 1024 * 1024;
const SUPPORTED_DOCUMENT_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);
const SUPPORTED_DOCUMENT_EXTENSIONS = new Set(["pdf", "jpg", "jpeg", "png", "webp", "heic", "heif"]);

function validateSupportingDocument(file) {
  if (!file) return null;
  if (file.size > MAX_SUPPORTING_DOCUMENT_BYTES) return "Choose a document smaller than 10 MB";
  const extension = String(file.name || "").split(".").pop()?.toLowerCase();
  if (!SUPPORTED_DOCUMENT_TYPES.has(file.type) && !SUPPORTED_DOCUMENT_EXTENSIONS.has(extension)) {
    return "Use a PDF, JPG, PNG, WEBP, or HEIC supporting document";
  }
  return null;
}

export default function HealthExemptionSheet({
  open,
  onOpenChange,
  child,
  types,
  presetTypeId,
  onSaved,
}) {
  const [recordTypeId, setRecordTypeId] = useState("");
  const [basis, setBasis] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const applicableTypes = useMemo(
    () => (child ? (types || []).filter((type) => type.is_required && typeAppliesToChild(type, child)) : []),
    [child, types],
  );

  useEffect(() => {
    if (!open) return;
    setRecordTypeId(presetTypeId || applicableTypes[0]?.id || "");
    setBasis("");
    setExpiresOn("");
    setNotes("");
    setFile(null);
  }, [open, presetTypeId, applicableTypes]);

  if (!child) return null;

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!recordTypeId) return toast.error("Choose a requirement");
    if (basis.trim().length < 2) return toast.error("Please add a brief exemption basis");
    const documentError = validateSupportingDocument(file);
    if (documentError) return toast.error(documentError);
    setSaving(true);
    try {
      const row = await submitHealthExemption({
        childId: child.id,
        recordTypeId,
        basis: basis.trim(),
        expiresOn,
        notes: notes.trim(),
      });
      if (file && row?.id) await uploadHealthExemptionDocument({ exemptionId: row.id, file });
      toast.success("Exemption request submitted for review");
      onOpenChange(false);
      await onSaved?.();
    } catch (error) {
      toast.error(error.message || "Could not submit the exemption request");
    } finally {
      setSaving(false);
    }
  };
  const input = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="health-exemption-sheet">
        <SheetHeader className="text-left">
          <SheetTitle>Request an exemption review</SheetTitle>
          <SheetDescription>
            Submit a request for {child.first_name}. Your center reviews it before it changes the child's compliance status.
          </SheetDescription>
        </SheetHeader>
        <form className="mt-5 space-y-4" onSubmit={onSubmit}>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Requirement</span>
            <select data-testid="health-exemption-type" value={recordTypeId} onChange={(event) => setRecordTypeId(event.target.value)} className={input}>
              {applicableTypes.length === 0 && <option value="">No applicable requirements</option>}
              {applicableTypes.map((type) => <option key={type.id} value={type.id}>{type.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Brief basis</span>
            <textarea data-testid="health-exemption-basis" value={basis} onChange={(event) => setBasis(event.target.value)} rows={3} className={input} placeholder="Share the information your center needs to begin a review." />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Expires on <span className="font-normal normal-case text-neutral-400">(optional)</span></span>
            <input data-testid="health-exemption-expires" type="date" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} className={input} />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Supporting document <span className="font-normal normal-case text-neutral-400">(optional)</span></span>
            <input data-testid="health-exemption-file" type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(event) => setFile(event.target.files?.[0] || null)} className="block w-full text-sm text-neutral-600 file:mr-3 file:rounded-full file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-primary-hover" />
            <span className="mt-1 block text-[11px] text-neutral-500">PDF or image, up to 10 MB.</span>
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Note to the center <span className="font-normal normal-case text-neutral-400">(optional)</span></span>
            <textarea data-testid="health-exemption-note" value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} className={input} />
          </label>
          <button type="submit" disabled={saving || applicableTypes.length === 0} data-testid="health-exemption-submit" className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">
            {saving ? "Submitting…" : "Submit for review"}
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
